/**
 * Resolve the effective chart theme + fibonacci levels for a given
 * tab by walking the scope cascade:
 *
 *   chart-tab → workspace → user → built-in defaults
 *
 * Each scope either OWNS a full config or inherits — no field-
 * level cross-scope merging. First scope with a defined value
 * wins.
 *
 * For theme: workspace always has SOMETHING in `prefs.theme`
 * (it's seeded to a preset on hydrate), so the user-scope rung
 * only matters when the user has set defaults that should
 * propagate to fresh workspaces. Today workspace.theme is the
 * effective floor unless the user has explicitly chosen User
 * scope and saved.
 *
 * For fib: user-scope IS the persisted /api/fib_levels — the
 * existing fibLevelsSlice already loads it. Workspace + chart
 * sit on top.
 */

import type { RootState } from "../store";
import type { ChartTheme, LineStyleName } from "../slices/uiSlice";
import type { AppTheme } from "../slices/appearanceSlice";
import type { FibLevel } from "@/api/types";

/** The built-in dark chart background shared by both presets. When a
 *  chart still carries this value it's treated as "auto" — the surface
 *  follows the app's light/dark UI theme rather than being pinned dark. */
export const DEFAULT_DARK_CHART_BG = "#0c111c";
/** Default light chart background (matches the app's light --surface). */
export const DEFAULT_LIGHT_CHART_BG = "#ffffff";

/** Non-data chart "chrome": the page background plus the axis text, grid
 *  and axis-border colors that have to read legibly against it. Candle
 *  and line colors are NOT part of this — they come from the chart theme
 *  and are left untouched. */
export interface ChartChrome {
  bg: string;
  textColor: string;
  gridColor: string;
  axisBorder: string;
}

/** Rough perceived-lightness test so the axis text / grid flip to dark
 *  ink on any light background (the default white, or a custom light bg
 *  the user picked). Tolerant of #rgb / #rrggbb / rgb() / rgba(); any
 *  unparseable value is treated as dark (the historical default). */
function isBgLight(color: string): boolean {
  const c = color.trim().toLowerCase();
  let r = 0;
  let g = 0;
  let b = 0;
  if (c.startsWith("#")) {
    const hex = c.slice(1);
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length >= 6) {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    } else {
      return false;
    }
  } else {
    const m = c.match(/rgba?\(([^)]+)\)/);
    if (!m) return false;
    const parts = m[1].split(",").map((s) => parseFloat(s));
    [r, g, b] = parts;
  }
  if ([r, g, b].some((v) => Number.isNaN(v))) return false;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6;
}

/** Whether the tab has an explicit chart-theme override for the CURRENT
 *  app UI mode (at tab or user scope). When false, the effective theme is
 *  a cross-mode fallback (the dark-authored base / preset), which the
 *  chrome resolver treats specially in light mode so a customised dark
 *  background doesn't leak onto the light chart. */
export function selectHasModeThemeOverride(
  state: RootState,
  tabId: string,
): boolean {
  const tab = state.ui.byTab[tabId];
  if (state.appearance.theme === "light") {
    return !!(tab?.themeLight || state.userPrefs.themeLight);
  }
  return !!(tab?.theme || state.userPrefs.theme);
}

/** Resolve the chart chrome for a chart-theme background against the
 *  app's light/dark UI theme. A default (auto) background tracks the app
 *  theme — white in light mode, the dark surface in dark mode — so the
 *  chart re-skins along with the rest of the UI. A user-customised
 *  background is honoured as-is; the text/grid/axis are then chosen for
 *  legibility against whichever background won.
 *
 *  `hasModeOverride` guards the light-mode fallback: when the user has NOT
 *  customised the chart for light mode, the effective theme is the
 *  dark-authored base (possibly with a custom dark bg). Rather than render
 *  that dark bg on the light chart, force the default light background.
 *  An explicit light override is always honoured as-is. */
export function selectChartChrome(
  themeBg: string,
  appTheme: AppTheme,
  hasModeOverride: boolean = true,
): ChartChrome {
  let bg: string;
  if (appTheme !== "light") {
    // Dark mode: use the theme bg as-is (the default sentinel is dark).
    bg = themeBg;
  } else if (!hasModeOverride) {
    // Light mode, no light-specific override → don't inherit a dark base.
    bg = DEFAULT_LIGHT_CHART_BG;
  } else {
    // Light mode with an explicit override → respect it, but a theme still
    // sitting on the default sentinel auto-whitens.
    bg =
      themeBg.trim().toLowerCase() === DEFAULT_DARK_CHART_BG
        ? DEFAULT_LIGHT_CHART_BG
        : themeBg;
  }
  return isBgLight(bg)
    ? { bg, textColor: "#3c4658", gridColor: "#e9edf3", axisBorder: "#d8dee7" }
    : { bg, textColor: "#e6e8eb", gridColor: "#1c2029", axisBorder: "#2a2f3a" };
}

/** Per-tab chart theme.
 *
 *   tab.theme → user.theme → built-in preset default
 *
 * The simplified 2-scope model dropped the workspace-wide rung —
 * each chart now owns its theme (migrated on hydrate from any
 * legacy workspace.prefs.theme). User scope is the fallback for
 * tabs that haven't been customised; built-in defaults catch the
 * fresh-user case. */
export function selectEffectiveTheme(
  state: RootState,
  tabId: string,
): ChartTheme {
  const tab = state.ui.byTab[tabId];
  // Themes are stored per app UI mode: a user can customise dark and
  // light independently. Pick the slot matching the current app theme;
  // an un-customised mode falls through to the preset (whose default
  // bg auto-whitens in light via selectChartChrome).
  const light = state.appearance.theme === "light";
  if (light) {
    if (tab?.themeLight) return tab.themeLight;
    if (state.userPrefs.themeLight) return state.userPrefs.themeLight;
  } else {
    if (tab?.theme) return tab.theme;
    if (state.userPrefs.theme) return state.userPrefs.theme;
  }
  // Fall through to built-in preset. The slice's initial prefs.theme
  // is still typed (kept for legacy round-tripping), so this matches
  // its default — Red/Green at startup.
  return state.ui.prefs.theme;
}

/** Price Range tool styling. Unlike other drawing-tool styles (per-tab via
 *  the chart-theme cascade), this is a PER-USER, PER-MODE setting: it reads
 *  straight from the user-scope theme slot for the current app UI mode
 *  (light/dark) and so applies to every chart regardless of any per-tab theme
 *  override. Absent slot / fields → defaults (auto direction colours). */
export interface PriceRangeStyle {
  color?: string;
  textMatch: boolean;
  textColor?: string;
  labelSide: "left" | "right";
  boxWidth: number;
  labelWeight: "normal" | "bold";
  arrow: boolean;
  midline: boolean;
  midlineStyle: LineStyleName;
  midlineWidth: number;
}

/** Resolve the Price Range tool's styling for a tab + the current app mode.
 *
 * Per-KEY cascade: per-tab override (Workspace scope) → user slot (User
 * defaults) → built-in default. A per-key cascade (not the whole-object theme
 * pick) so a tab that overrode one price-range field still inherits the rest
 * from the user slot, and so the tool's Workspace scope actually applies. */
export function selectPriceRangeStyle(
  state: RootState,
  tabId: string,
): PriceRangeStyle {
  const light = state.appearance.theme === "light";
  const tab: ChartTheme | null | undefined = light
    ? state.ui.byTab[tabId]?.themeLight
    : state.ui.byTab[tabId]?.theme;
  const user: ChartTheme | null | undefined = light
    ? state.userPrefs.themeLight
    : state.userPrefs.theme;

  const match = tab?.priceRangeTextMatch ?? user?.priceRangeTextMatch;
  const side = tab?.priceRangeLabelSide ?? user?.priceRangeLabelSide;
  const weight = tab?.priceRangeLabelWeight ?? user?.priceRangeLabelWeight;
  return {
    color: tab?.priceRangeColor ?? user?.priceRangeColor,
    textMatch: match !== false,
    textColor: tab?.priceRangeTextColor ?? user?.priceRangeTextColor,
    labelSide: side === "left" ? "left" : "right",
    boxWidth: tab?.priceRangeBoxWidth ?? user?.priceRangeBoxWidth ?? 2,
    labelWeight: weight === "normal" ? "normal" : "bold",
    arrow: (tab?.priceRangeArrow ?? user?.priceRangeArrow) !== false,
    midline: (tab?.priceRangeMidline ?? user?.priceRangeMidline) !== false,
    midlineStyle:
      tab?.priceRangeMidlineStyle ?? user?.priceRangeMidlineStyle ?? "dashed",
    midlineWidth:
      tab?.priceRangeMidlineWidth ?? user?.priceRangeMidlineWidth ?? 1,
  };
}

/** Per-tab fibonacci levels. Falls all the way through to the
 *  user-scope (fibLevelsSlice.levels — what `/api/fib_levels`
 *  loaded). Defensive `?? []` for the impossible case where
 *  even that's missing during a brief boot window. */
export function selectEffectiveFibLevels(
  state: RootState,
  tabId: string,
): FibLevel[] {
  const tab = state.ui.byTab[tabId];
  if (tab?.fibLevels) return tab.fibLevels;
  if (state.ui.prefs.fibLevels) return state.ui.prefs.fibLevels;
  return state.fibLevels.levels ?? [];
}

/** Per-tab fib stroke width — same cascade as the levels list,
 *  with a final fallback to the User-scope value (which itself
 *  defaults to 1 px until /api/fib_levels resolves). */
export function selectEffectiveFibLineWidth(
  state: RootState,
  tabId: string,
): number {
  const tab = state.ui.byTab[tabId];
  if (typeof tab?.fibLineWidth === "number") return tab.fibLineWidth;
  return state.fibLevels.lineWidth;
}

/** Per-tab fib stroke style. */
export function selectEffectiveFibLineStyle(
  state: RootState,
  tabId: string,
): LineStyleName {
  const tab = state.ui.byTab[tabId];
  if (tab?.fibLineStyle) return tab.fibLineStyle;
  return state.fibLevels.lineStyle;
}

/** Diagonal/trend-line appearance, default visibility, and default
 *  label side. These are user-scope global fib settings (no per-tab
 *  override) — read straight from the fibLevels slice. Per-fib
 *  drawings can still override show/hide + label side individually. */
export function selectEffectiveFibDiagonal(state: RootState) {
  return state.fibLevels.diagonal;
}
export function selectEffectiveFibShowDiagonal(state: RootState): boolean {
  return state.fibLevels.showDiagonal;
}
export function selectEffectiveFibLabelSide(
  state: RootState,
): "left" | "right" {
  return state.fibLevels.labelSide;
}
